import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import logo from "../../assets/logo.png";
import bgImage from "../../assets/bg.png";
import { toast } from "react-toastify";
import axios from "axios";

export default function AccessTokenEnter() {
    const [token, setToken] = useState("");
    const [username, setUsername] = useState("");
    const [email, setEmail] = useState("");
    const [redirect, setRedirect] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();

        const trimmedToken = token.trim();
        const trimmedUsername = username.trim();
        const trimmedEmail = email.trim();

        if (!trimmedToken || !trimmedUsername || !trimmedEmail) return;

        try {
            const resp = await axios.get(
                `${process.env.REACT_APP_SERVER_URL}/external/verify/${encodeURIComponent(
                    trimmedToken
                )}`,
                {
                    headers: {
                        Authorization: `Bearer ${trimmedToken}`,
                    },
                }
            );

            // Use resp to determine validity
            if (!resp?.data?.valid) {
                toast.error(resp?.data?.error || "Invalid access token.");
                return;
            }

            // Token is valid → store and redirect
            localStorage.setItem("externalAccessToken", trimmedToken);
            localStorage.setItem("username", trimmedUsername);
            localStorage.setItem("email", trimmedEmail);

            setRedirect(true);
        } catch (err) {
            // Network / unexpected error
            toast.error("Invalid access token");
        }
    };

    if (redirect) {
        return <Navigate to="/dashboard-external" replace />;
    }

    const isDisabled =
        !token.trim() || !username.trim() || !email.trim();

    return (
        <div
            className="min-h-[100svh] flex items-center justify-center bg-gray-100 px-4 bg-cover bg-center"
            style={{ backgroundImage: `url(${bgImage})` }}
        >
            <motion.div
                initial={{ opacity: 0, y: 24, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="w-full max-w-xl"
            >
                <motion.div
                    initial={{ boxShadow: "0 0 0 rgba(0,0,0,0)" }}
                    animate={{ boxShadow: "0 18px 45px rgba(15,23,42,0.25)" }}
                    transition={{ delay: 0.1, duration: 0.4 }}
                    className="overflow-hidden rounded-2xl border border-gray-200/70 bg-black/70 backdrop-blur-sm"
                >
                    <div className="p-6 sm:p-8 space-y-6">
                        {/* Logo + app name */}
                        <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded bg-blue-600">
                                <img
                                    src={logo}
                                    alt="Logo"
                                    className="h-7 w-7 rounded bg-white/10 p-0.5"
                                />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-sm font-semibold uppercase tracking-wide text-blue-200">
                                    Data Enhancer
                                </span>
                                <span className="text-xs text-gray-300">
                                    External token sign-in
                                </span>
                            </div>
                        </div>

                        {/* Title + description */}
                        <div className="space-y-1 text-left">
                            <h1 className="text-xl sm:text-2xl font-semibold text-white">
                                Enter your details
                            </h1>
                            <p className="text-sm text-gray-200">
                                Add your name, email, and access token to continue.
                            </p>
                        </div>

                        {/* Form */}
                        <motion.form
                            onSubmit={handleSubmit}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.18, duration: 0.35 }}
                            className="space-y-4"
                        >
                            {/* Username */}
                            <label className="flex flex-col gap-2 text-left">
                                <span className="text-xs font-medium uppercase tracking-wide text-gray-200">
                                    Name
                                </span>
                                <input
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    placeholder="Enter your name"
                                    className="w-full rounded-lg border border-gray-500/60 bg-black/40 px-3 py-2.5 text-sm text-white placeholder:text-gray-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/50 transition"
                                />
                            </label>

                            {/* Email */}
                            <label className="flex flex-col gap-2 text-left">
                                <span className="text-xs font-medium uppercase tracking-wide text-gray-200">
                                    Email
                                </span>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="w-full rounded-lg border border-gray-500/60 bg-black/40 px-3 py-2.5 text-sm text-white placeholder:text-gray-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/50 transition"
                                />
                            </label>

                            {/* Access token */}
                            <label className="flex flex-col gap-2 text-left">
                                <span className="text-xs font-medium uppercase tracking-wide text-gray-200">
                                    Access token
                                </span>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={token}
                                        onChange={(e) => setToken(e.target.value)}
                                        placeholder="Paste your token here"
                                        className="w-full rounded-lg border border-gray-500/60 bg-black/40 px-3 py-2.5 text-sm text-white placeholder:text-gray-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/50 transition"
                                    />
                                    {/* Subtle status dot */}
                                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                        <span className="h-2 w-2 rounded-full bg-blue-400 shadow-[0_0_10px_rgba(96,165,250,0.9)]" />
                                    </div>
                                </div>
                            </label>

                            <motion.button
                                type="submit"
                                disabled={isDisabled}
                                whileHover={{ scale: isDisabled ? 1 : 1.015 }}
                                whileTap={{ scale: isDisabled ? 1 : 0.98 }}
                                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-500 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-blue-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/70 disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none"
                            >
                                Continue
                            </motion.button>

                            {/* Switch back to main sign-in */}
                            <button
                                type="button"
                                onClick={() => navigate("/")}
                                className="w-full text-sm text-gray-300 hover:text-white mt-2 underline-offset-4 hover:underline transition"
                            >
                                Go to standard sign-in
                            </button>
                        </motion.form>
                    </div>
                </motion.div>
            </motion.div>
        </div>
    );
}
